/**
 * CountdownTimer Component
 *
 * Urgency-building countdown display.
 * Features:
 * - Animated number flip effect
 * - Configurable time units (hours, minutes, seconds)
 * - Pulsing effect for added urgency
 * - Customizable styling
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion';

export interface CountdownTimerProps {
  /** Total seconds to display */
  totalSeconds: number;
  /** Frame when animation should start */
  startFrame?: number;
  /** Show hours unit */
  showHours?: boolean;
  /** Background color for number boxes */
  boxBackgroundColor?: string;
  /** Text color */
  textColor?: string;
  /** Label color */
  labelColor?: string;
  /** Font size for numbers */
  fontSize?: number;
  /** Gap between units */
  gap?: number;
  /** Enable pulse animation */
  enablePulse?: boolean;
}

interface TimeUnit {
  value: number;
  label: string;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  totalSeconds,
  startFrame = 0,
  showHours = true,
  boxBackgroundColor = 'rgba(0,0,0,0.3)',
  textColor = '#ffffff',
  labelColor = 'rgba(255,255,255,0.7)',
  fontSize = 32,
  gap = 10,
  enablePulse = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Fade in animation
  const opacity = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Slide up animation
  const translateY = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [20, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Pulse effect (subtle scale breathing)
  const pulseScale = enablePulse
    ? 1 + Math.sin(relativeFrame * 0.15) * 0.02
    : 1;

  // Calculate time units
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const timeUnits: TimeUnit[] = [];

  if (showHours && hours > 0) {
    timeUnits.push({ value: hours, label: 'HRS' });
  }
  timeUnits.push({ value: minutes, label: 'MIN' });
  timeUnits.push({ value: seconds, label: 'SEC' });

  return (
    <div
      style={{
        display: 'flex',
        gap,
        opacity,
        transform: `translateY(${translateY}px) scale(${pulseScale})`,
      }}
    >
      {timeUnits.map(({ value, label }, index) => (
        <React.Fragment key={label}>
          <div
            style={{
              backgroundColor: boxBackgroundColor,
              padding: '10px 15px',
              borderRadius: 10,
              textAlign: 'center',
              minWidth: 60,
            }}
          >
            <div
              style={{
                color: textColor,
                fontSize,
                fontWeight: 700,
                fontFamily: 'Inter, sans-serif',
                lineHeight: 1.2,
              }}
            >
              {String(value).padStart(2, '0')}
            </div>
            <div
              style={{
                color: labelColor,
                fontSize: fontSize * 0.45,
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                marginTop: 4,
              }}
            >
              {label}
            </div>
          </div>
          {/* Colon separator */}
          {index < timeUnits.length - 1 && (
            <div
              style={{
                color: textColor,
                fontSize,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.5,
              }}
            >
              :
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
